<template>
    <div>
        <MatchInfo v-bind:event="event" class="featuredMatch" />
        <div class="content">
            <template v-if="isShowLiveMediaBlock">
                <div
                    class="content_column"
                    v-bind:class="{
                        'content_column-liveVideo': activeTab === TabName.LiveVideo,
                        'content_column-liveCourt': activeTab === TabName.LiveCourt,
                    }"
                >
                    <Navbar v-bind:isFlush="true">
                        <template #topRow>
                            <NavbarMain>
                                <template
                                    v-for="item in navbarItems"
                                    v-bind:key="item.name"
                                >
                                    <NavbarItem
                                        v-if="item.isShow"
                                        v-gtm:label="item.gtmLabel"
                                        v-gtm:@click
                                        v-bind:name="item.name"
                                        v-bind:isActive="item.isActive"
                                        v-bind:onclick="item.onclick"
                                    />
                                </template>
                            </NavbarMain>
                        </template>
                    </Navbar>
                    <div
                        class="iframeWrapper"
                        v-bind:class="{
                            'iframeWrapper-liveVideo': activeTab === TabName.LiveVideo,
                            'iframeWrapper-liveCourt': activeTab === TabName.LiveCourt,
                        }"
                    >
                        <Preview
                            v-if="activeTab === TabName.LiveVideo"
                            v-gtm:label="'LiveVideo'"
                            v-gtm:@click
                            v-on:click="openLiveStream"
                        >
                            <component
                                v-bind:is="streamDisplay.component"
                                v-if="streamDisplay"
                                v-bind:key="streamDisplay.key"
                                v-bind:videoUrl="streamDisplay.props.videoUrl"
                                v-bind:betRadar="streamDisplay.props.betRadar!"
                                v-bind:gLive="streamDisplay.props.gLive!"
                                v-bind:betConstruct="streamDisplay.props.betConstruct"
                                v-bind:betGenius="streamDisplay.props.betGenius"
                                v-bind:isEmbedOnMainPage="true"
                            />
                        </Preview>
                        <Preview
                            v-if="activeTab === TabName.LiveCourt"
                            v-gtm:label="'LiveCourt'"
                            v-gtm:@click
                            v-on:click="openLiveCourt"
                        >
                            <LiveCourtPreview v-bind:liveCourtMatchId="event.liveCourtMatchId" />
                        </Preview>
                    </div>
                </div>
            </template>
            <div class="content_column">
                <MarketsPanel
                    v-bind:class="{ live: event.isLive }"
                    v-bind:marketTypes="marketTypes"
                    v-bind:event="event"
                    v-bind:displaySection="DisplaySection.TournamentCenter_FeaturedMatches"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, toRefs, watchEffect } from 'vue';
    import Preview from '@/components/common/Preview.vue';
    import Navbar from '@/components/common/navbar/Navbar.vue';
    import NavbarItem from '@/components/common/navbar/NavbarItem.vue';
    import NavbarMain from '@/components/common/navbar/NavbarMain.vue';
    import LiveCourtPreview from '@/components/liveCourt/LiveCourtPreview.vue';
    import MarketsPanel from '@/components/tournamentCenter/MarketsPanel.vue';
    import MatchInfo from '@/components/tournamentCenter/MatchInfo.vue';
    import { TOURNAMENT_CENTER_ALL_MARKET_TYPES, TOURNAMENT_CENTER_FULLTIME_MARKET_TYPES } from '@/components/tournamentCenter/config';
    import { useLiveStream } from '@/composable/liveStream/useLiveStream';
    import { useLiveCourt } from '@/composable/useLiveCourt';
    import { i18n } from '@/core/lib/i18n';
    import { DisplaySection } from '@/interface/enum';
    import type { ITournamentEvent } from '@/interface/tournamentCenter';

    enum TabName {
        LiveVideo,
        LiveCourt,
    }

    const props = defineProps<{
        event: ITournamentEvent;
    }>();

    const { event } = toRefs(props);
    const { isLiveCourtAvailable, openLiveCourt } = useLiveCourt(event);
    const { isLiveStreamPlayable, streamDisplay, openLiveStream: _openLiveStream } = useLiveStream(event);

    const firstAvailableTab = computed(() => {
        if (isLiveStreamPlayable.value) return TabName.LiveVideo;
        if (isLiveCourtAvailable.value) return TabName.LiveCourt;
        return null;
    });

    const activeTab = ref<TabName>(firstAvailableTab.value ?? TabName.LiveVideo);

    const isActiveTabEnabled = computed(() => (activeTab.value === TabName.LiveVideo
        ? isLiveStreamPlayable.value
        : isLiveCourtAvailable.value));

    watchEffect(() => {
        if (isActiveTabEnabled.value || firstAvailableTab.value === null) {
            return;
        }

        activeTab.value = firstAvailableTab.value;
    });

    const navbarItems = computed(() => [
        {
            name: i18n.t('tournament_center_live_video'),
            isActive: activeTab.value === TabName.LiveVideo,
            isShow: isLiveStreamPlayable.value,
            gtmLabel: 'LiveVideo',
            onclick: () => {
                activeTab.value = TabName.LiveVideo;
            },
        },
        {
            name: i18n.t('tournament_center_live_court'),
            isActive: activeTab.value === TabName.LiveCourt,
            isShow: isLiveCourtAvailable.value,
            gtmLabel: 'LiveCourt',
            onclick: () => {
                activeTab.value = TabName.LiveCourt;
            },
        },
    ]);

    // isLiveStreamPlayable is already soccer-gated for non-live events, so this also
    // surfaces the stream block for pre-match soccer without leaking other sports.
    const isShowLiveMediaBlock = computed(() => isLiveCourtAvailable.value || isLiveStreamPlayable.value);

    const marketTypes = computed(() => {
        if (isShowLiveMediaBlock.value) {
            return TOURNAMENT_CENTER_FULLTIME_MARKET_TYPES;
        }
        return TOURNAMENT_CENTER_ALL_MARKET_TYPES;
    });

    function openLiveStream() {
        _openLiveStream(event.value.id);
    }
</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/FeaturedMatch";
</style>
